import React, {useMemo} from "react";
import {Card, Col, Row} from "react-bootstrap";
import {CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {ABC_COLORS, formatEuro, formatNumber, formatPercent, GROUP_PALETTE, OTHER_GROUP_COLOR} from "./format.js";

const CLASS_LABELS = {
    A: "Klasse A – bis 80 % Umsatzanteil",
    B: "Klasse B – bis 95 % Umsatzanteil",
    C: "Klasse C – Rest",
};

// Die Kurve trägt einen Punkt je Artikel (bis zu mehreren Tausend) - für
// eine flüssige Darstellung wird gleichmäßig heruntergesampelt, der Verlauf
// bleibt dabei erhalten (monoton nach Rang sortiert).
function downsample(rows, maxPoints) {
    if (rows.length <= maxPoints) {
        return rows;
    }
    const step = Math.ceil(rows.length / maxPoints);
    const sampled = rows.filter((_, index) => index % step === 0);
    const last = rows[rows.length - 1];
    if (sampled[sampled.length - 1] !== last) {
        sampled.push(last);
    }
    return sampled;
}

/** #7 ABC-Analyse/Pareto - Kernaussage für die Lagerflächen-Frage: welcher
 * Anteil der Artikel trägt welchen Anteil des Umsatzes. */
export default function ParetoChart({filters}) {
    const {data, loading, error} = useStatisticsData("/statistics/pareto", filters);
    const curve = useMemo(() => downsample(data?.curve || [], 400), [data]);
    const classes = data?.classes || [];

    return (
        <ChartCard
            title="ABC-Analyse (Pareto)"
            help="Kumulierter Umsatzanteil über alle Artikel, absteigend nach Umsatz sortiert. Klasse A trägt die ersten 80 % des Umsatzes, Klasse B bis 95 %, Klasse C den Rest – typisch die Artikel, deren Lagerfläche ihr Umsatz nicht mehr rechtfertigt."
            loading={loading}
            error={error}
            empty={!loading && curve.length === 0}
            height={320}
        >
            <Row className="g-3">
                <Col lg={8}>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={curve} margin={{top: 8, right: 16, left: 0, bottom: 8}}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color, #dee2e6)" />
                            <XAxis
                                dataKey="rank"
                                tick={{fontSize: 12}}
                                axisLine={false}
                                tickLine={false}
                                label={{value: "Artikel-Rang nach Umsatz", position: "insideBottom", offset: -4, fontSize: 11}}
                            />
                            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{fontSize: 12}} axisLine={false} tickLine={false} width={50} />
                            <ReferenceLine y={80} stroke={OTHER_GROUP_COLOR} strokeDasharray="4 4" />
                            <ReferenceLine y={95} stroke={OTHER_GROUP_COLOR} strokeDasharray="4 4" />
                            <Tooltip
                                formatter={(value) => [formatPercent(value), "Kumulierter Umsatzanteil"]}
                                labelFormatter={(label, payload) => {
                                    const row = payload?.[0]?.payload;
                                    return row ? `Rang ${label} – ${row.article_number}` : `Rang ${label}`;
                                }}
                            />
                            <Line type="monotone" dataKey="cumulative_share_pct" stroke={GROUP_PALETTE[0]} strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </Col>
                <Col lg={4} className="d-flex flex-column gap-2">
                    {classes.map((entry) => (
                        <Card key={entry.class} className="border-0 bg-body-secondary">
                            <Card.Body className="py-2">
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="rounded-circle flex-shrink-0"
                                        style={{width: 10, height: 10, backgroundColor: ABC_COLORS[entry.class] || OTHER_GROUP_COLOR}}
                                        aria-hidden="true"
                                    />
                                    <span className="fw-semibold">{CLASS_LABELS[entry.class] || `Klasse ${entry.class}`}</span>
                                </div>
                                <div className="small text-muted mt-1">
                                    {formatNumber(entry.article_count)} Artikel ({formatPercent(entry.article_share_pct)}) ·{" "}
                                    {formatEuro(entry.net_amount)} ({formatPercent(entry.net_share_pct)})
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </Col>
            </Row>
        </ChartCard>
    );
}
